/**
 * Per-signal copy: what it is, why it matters, how to reduce it.
 * `phrase` names the signal in the summary sentence ("Your font list, … account for most of …").
 */
export interface Explanation {
  what: string;
  why: string;
  tips: string[];
  phrase?: string;
}

const NOTHING = 'Nothing practical to change: this follows from your browser and hardware.';
const KEEP_IF_NEEDED = 'Keep it if you need it. Your comfort matters more than a fraction of a bit.';

export const EXPLANATIONS: Record<string, Explanation> = {
  // ── Navigator ──
  'nav.userAgent': {
    what: 'The text your browser sends with every request and shows to scripts. It names the browser, its version and your operating system.',
    why: 'It splits users by browser, version and OS before any other signal is read. Rare browsers, outdated versions and unusual OS builds stand out.',
    tips: [
      'Keep your browser up to date: the current version is the most common one.',
      'Avoid user-agent spoofing extensions. A user agent that contradicts the rest of your fingerprint makes you more unique, not less.',
      'Tor Browser and Firefox with resistFingerprinting report a standardized user agent.',
    ],
    phrase: 'browser version',
  },
  'nav.uaData': {
    what: 'User-Agent Client Hints, basic set: a structured brand list, a mobile flag and the OS name (Chromium-based browsers only).',
    why: 'Mostly repeats the user agent. Low entropy by design.',
    tips: ['Nothing to change.'],
  },
  'nav.uaDataHigh': {
    what: 'Detailed Client Hints that any script can request: full browser version, OS version, CPU architecture and device model.',
    why: 'Restores the detail Chrome removed from the user agent string, and page scripts can read it without a permission prompt.',
    tips: [
      'Keep the browser updated so your full version matches most users.',
      'Firefox and Safari do not implement this API. Brave reduces some of the values.',
    ],
    phrase: 'detailed version hints',
  },
  'nav.platform': {
    what: 'A short OS and CPU label such as “Win32”, “MacIntel” or “Linux x86_64”.',
    why: 'Confirms your OS. If it contradicts the user agent, that reveals spoofing.',
    tips: ['Leave it alone. Changing it by itself creates inconsistencies that stand out.'],
  },
  'nav.vendor': {
    what: 'The browser vendor string: “Google Inc.”, “Apple Computer, Inc.”, or empty in Firefox.',
    why: 'Separates the three main engine families.',
    tips: ['Nothing to change.'],
  },
  'nav.product': {
    what: 'Legacy product and build identifiers (“Gecko” plus a frozen build date).',
    why: 'Frozen values that still differ between Firefox and everyone else: a small engine hint.',
    tips: ['Nothing to change.'],
  },
  'nav.webdriver': {
    what: 'A flag that is true when automation software such as Selenium, Puppeteer or Playwright controls the browser.',
    why: 'Bot detection relies on it. A true value is rare among real visitors.',
    tips: ['Normally false. If it is true and you are not running automation, check for unwanted software.'],
  },
  'nav.pdfViewerEnabled': {
    what: 'Whether the browser can display PDF files itself.',
    why: 'Almost always true on desktop. False hints at a mobile browser or a changed setting.',
    tips: ['Nothing to change.'],
  },
  'nav.plugins': {
    what: 'The browser plugin list. Modern browsers report the same fixed list of PDF viewers, or nothing.',
    why: 'Once among the strongest signals (Flash and Java versions). Browsers froze it, so it is now nearly worthless to trackers.',
    tips: ['Nothing to change: browsers neutralized this one.'],
  },
  'nav.mimeTypes': {
    what: 'File types handled by plugins, also frozen in modern browsers.',
    why: 'Mirrors the plugin list.',
    tips: ['Nothing to change.'],
  },
  'nav.vendorGlobals': {
    what: 'Browser-specific JavaScript objects such as window.chrome, window.safari or navigator.brave.',
    why: 'These reveal the real browser even when the user agent is spoofed.',
    tips: ['Do not rely on user-agent spoofing. These objects give the real browser away.'],
  },
  'nav.doNotTrack': {
    what: 'The Do Not Track preference. Few sites ever honored it, and Safari removed it.',
    why: 'Only a minority turn it on, so it adds a little identifying information and no legal protection.',
    tips: ['Prefer Global Privacy Control, which carries legal weight in some places. DNT mostly adds a bit to your fingerprint.'],
  },
  'nav.globalPrivacyControl': {
    what: 'Global Privacy Control: a signal asking sites not to sell or share your personal data.',
    why: 'Legally binding in California and several other US states. Only a minority turn it on, so it also adds a little to your fingerprint.',
    tips: [
      'Usually worth keeping on. The legal benefit outweighs the fraction of a bit it adds.',
      'Built into Brave, DuckDuckGo and Firefox (Settings → Privacy & Security).',
    ],
  },
  'nav.cookieEnabled': {
    what: 'Whether cookies are allowed at all.',
    why: 'Nearly everyone allows them, so “false” is rare.',
    tips: ['Block third-party cookies rather than all cookies. Most browsers now do that by default.'],
  },

  // ── Screen ──
  'screen.size': {
    what: 'Your screen resolution in CSS pixels.',
    why: 'Together with pixel ratio it narrows down your device model. Unusual resolutions and multi-monitor setups stand out.',
    tips: [
      'Tor Browser and Firefox resistFingerprinting report a rounded window size instead.',
      'Common resolutions (1920×1080, standard phone sizes) blend in better than rare ones.',
    ],
    phrase: 'screen resolution',
  },
  'screen.avail': {
    what: 'The screen area left after the taskbar or dock, plus its offset when you have several monitors.',
    why: 'Reveals your taskbar or dock size and position and how your monitors are arranged.',
    tips: ['Default taskbar and dock settings are the most common.'],
  },
  'screen.inner': {
    what: 'The size of the page area inside the browser window.',
    why: 'Very distinctive, but it changes whenever you resize, so trackers use it to link activity within one session.',
    tips: ['Keep the window maximized, or use Firefox letterboxing, which rounds it.'],
  },
  'screen.outer': {
    what: 'The full browser window size, including toolbars.',
    why: 'Reveals toolbar and bookmark-bar height. Like the viewport, it changes when you resize.',
    tips: ['Same as the viewport: maximize, or letterbox.'],
  },
  'screen.colorDepth': {
    what: 'Bits per pixel of color output.',
    why: 'It is 24 for almost everyone. 30 or 48 indicates HDR or wide-color hardware.',
    tips: ['Nothing to change.'],
  },
  'screen.dpr': {
    what: 'The ratio of physical pixels to CSS pixels, such as 2 on Retina displays.',
    why: 'Distinguishes display types and reveals your OS scaling or page zoom.',
    tips: ['Use 100% page zoom. Unusual zoom levels produce rare values.'],
  },
  'screen.orientation': {
    what: 'Portrait or landscape, and the rotation angle.',
    why: 'Mainly separates phones and tablets from desktops.',
    tips: ['Nothing to change.'],
  },
  'screen.spoofCheck': {
    what: 'A check for whether your reported screen size looks rounded or faked.',
    why: 'Spoofed sizes from Tor Browser or resistFingerprinting are shared by many users, which is the point.',
    tips: ['For this protection, use Tor Browser, or Firefox with resistFingerprinting and letterboxing.'],
  },

  // ── Hardware ──
  'hw.cores': {
    what: 'The number of logical CPU cores.',
    why: 'Values range from 2 to 64 and beyond. High or odd counts narrow down your hardware.',
    tips: ['Brave and resistFingerprinting report fake or standardized values. Chrome and Safari offer no setting.'],
    phrase: 'CPU core count',
  },
  'hw.memory': {
    what: 'Approximate device memory in GB, rounded and capped by the browser (Chromium only).',
    why: 'Coarse by design, but it still sorts users into a few buckets.',
    tips: ['Firefox and Safari do not expose it.'],
  },
  'hw.touch': {
    what: 'How many simultaneous touch points your screen supports.',
    why: 'Separates touch laptops and tablets from mouse-only desktops.',
    tips: ['Nothing to change.'],
  },
  'hw.battery': {
    what: 'Battery level and charging state, from a deprecated Chromium API.',
    why: 'Changes too quickly to track you across days, but it was once used to link visits made minutes apart.',
    tips: ['Firefox and Safari removed this API.'],
  },
  'hw.connection': {
    what: 'Network type and rounded speed estimates (Chromium only).',
    why: 'It fluctuates, so it mostly links visits made close together.',
    tips: ['Firefox and Safari do not expose it.'],
  },
  'hw.gamepads': {
    what: 'Game controllers this page can see.',
    why: 'Browsers hide them until you press a button, so trackers rarely get them.',
    tips: ['Nothing to change.'],
  },

  // ── Locale ──
  'locale.language': {
    what: 'Your browser’s primary language.',
    why: 'Narrows you down to a language community.',
    tips: ['English (en-US) is the most common setting worldwide, if that works for you.'],
  },
  'locale.languages': {
    what: 'Your preferred languages, in order. Every site also receives them in the Accept-Language header.',
    why: 'Multilingual setups are distinctive: the exact combination and order narrow the crowd quickly.',
    tips: ['Keep only the languages you actually need.', 'Firefox resistFingerprinting can ask for English versions of pages.'],
    phrase: 'language list',
  },
  'locale.timezone': {
    what: 'Your time zone, such as Europe/Berlin.',
    why: 'Reveals your rough location, and a VPN does not change it.',
    tips: ['Tor Browser and resistFingerprinting report UTC.', 'Rare time zones identify you more than populous ones.'],
    phrase: 'time zone',
  },
  'locale.tzOffset': {
    what: 'Minutes between your local time and UTC.',
    why: 'Follows from your time zone. Shown for completeness.',
    tips: ['Same as the time zone.'],
  },
  'locale.intlOptions': {
    what: 'Defaults used by the Intl API: locale, calendar, numbering system and 12- or 24-hour clock.',
    why: 'Reveals OS regional settings, which can differ from the browser language.',
    tips: ['The default regional settings for your language are the most common.'],
  },
  'locale.formats': {
    what: 'How a fixed number, price and date are formatted with your defaults.',
    why: 'Shows regional choices such as decimal separators and date order.',
    tips: ['Same as the Intl defaults.'],
  },
  'locale.tzMatch': {
    what: 'Whether your time zone is on the continent your language suggests.',
    why: 'Informational. A mismatch can mean travel, a VPN, or a language used abroad. Trackers use it to spot VPN users.',
    tips: ['If you use a VPN to hide your location, your time zone still gives it away. Tor Browser handles this.'],
  },

  // ── Canvas ──
  'canvas.hash': {
    what: 'A hash of an image your browser drew: text in several fonts, emoji, blended shapes and gradients.',
    why: 'GPU, driver, OS font rendering and anti-aliasing all leave traces in the pixels. The result differs between machines but stays the same on yours.',
    tips: [
      'Brave and Firefox (Enhanced Tracking Protection: Strict) add per-session noise.',
      'Tor Browser and resistFingerprinting block or randomize canvas readback.',
      'The CanvasBlocker extension for Firefox randomizes it, but extensions can themselves be detected.',
    ],
    phrase: 'canvas rendering',
  },
  'canvas.emoji': {
    what: 'A hash of a row of emoji drawn on a canvas.',
    why: 'Emoji fonts differ between operating systems and OS versions, so this is a precise OS-version signal.',
    tips: ['The same protections as the canvas hash apply.'],
    phrase: 'emoji rendering',
  },
  'canvas.randomization': {
    what: 'Checks whether your browser randomizes canvas output. The same image is drawn twice, and a test pattern with known pixels is read back.',
    why: 'Randomized output makes the canvas hash useless for tracking you across sites or sessions.',
    tips: ['Brave (default), Firefox ETP Strict, Safari’s Advanced Fingerprinting Protection and Tor Browser all do this.'],
  },

  // ── WebGL ──
  'webgl.support': {
    what: 'Whether WebGL 1 and WebGL 2 are available.',
    why: 'Disabled WebGL is uncommon, so it is slightly identifying.',
    tips: ['Disabling WebGL removes several strong signals, but it breaks maps and 3D sites.'],
  },
  'webgl.vendor': {
    what: 'Your GPU vendor as reported by WebGL.',
    why: 'Sorts users by GPU maker: Intel, NVIDIA, AMD, Apple, Qualcomm and so on.',
    tips: ['Safari and Firefox report coarse values.'],
  },
  'webgl.renderer': {
    what: 'Your GPU model and graphics backend.',
    why: 'Often names the exact GPU model and driver API, which makes it one of the most distinctive single strings in a fingerprint.',
    tips: [
      'Safari reports a generic “Apple GPU”. Firefox reports a simplified name.',
      'Chrome and Edge expose the full model and offer no setting to hide it.',
    ],
    phrase: 'GPU name',
  },
  'webgl.extensions': {
    what: 'The WebGL extensions your GPU and driver support.',
    why: 'The list depends on GPU, driver and browser version.',
    tips: ['There is no practical way to change it short of disabling WebGL.'],
  },
  'webgl.params': {
    what: 'Hardware limits such as maximum texture size and shader precision.',
    why: 'Together they distinguish GPU families and drivers.',
    tips: ['Same as the extension list.'],
  },
  'webgl.render': {
    what: 'A hash of a shaded triangle rendered by your GPU.',
    why: 'Floating-point and anti-aliasing differences between GPUs and drivers show up in the pixels.',
    tips: ['Brave and some extensions add noise to WebGL readback.'],
    phrase: 'WebGL rendering',
  },
  'webgl.randomization': {
    what: 'Checks whether WebGL readback is randomized, using a repeated render and a known-color test.',
    why: 'Randomization makes the WebGL rendering hash useless for tracking.',
    tips: ['Brave does this by default.'],
  },

  // ── Audio ──
  'audio.hash': {
    what: 'A number computed from a tone run through the audio engine (oscillator into compressor), rendered silently in memory.',
    why: 'Small differences in each platform’s audio math produce a stable value. Researchers found it on real tracking sites.',
    tips: ['Brave randomizes it. Tor Browser and resistFingerprinting standardize parts of the audio stack.'],
    phrase: 'audio processing',
  },
  'audio.sampleRate': {
    what: 'Your audio output device’s sample rate, often 44.1 or 48 kHz.',
    why: 'Varies by device, and changes when you switch outputs.',
    tips: [NOTHING],
  },
  'audio.baseLatency': {
    what: 'The audio output’s processing latency.',
    why: 'Depends on your hardware and the OS audio stack.',
    tips: [NOTHING],
  },
  'audio.randomization': {
    what: 'Checks whether audio output is randomized, using a repeated render and known samples.',
    why: 'Randomization makes the audio fingerprint useless for tracking.',
    tips: ['Brave does this by default.'],
  },

  // ── Fonts ──
  'fonts.list': {
    what: 'Fonts detected from a fixed candidate list, by measuring whether text changes width when a font is requested.',
    why: 'Fonts installed by Office, Adobe, design tools or language packs make your list distinctive. On desktop it is often the biggest single contributor.',
    tips: [
      'Uninstall fonts you no longer use.',
      'Safari and Firefox resistFingerprinting only expose system fonts.',
      'Brave randomizes which fonts each site can see.',
    ],
    phrase: 'font list',
  },
  'fonts.osGuess': {
    what: 'Your OS, inferred from which OS-specific fonts are present.',
    why: 'Shows that fonts alone reveal your OS, and can expose a spoofed user agent.',
    tips: ['Nothing to change.'],
  },
  'fonts.apis': {
    what: 'Whether font APIs exist: document.fonts, and the Local Font Access API. The latter is not called here because it would show a prompt.',
    why: 'Chromium’s queryLocalFonts() can list every installed font, but only after you grant permission.',
    tips: ['Deny font-access prompts unless you trust the site.'],
  },

  // ── Media ──
  'media.devices': {
    what: 'How many microphones, speakers and cameras are attached. Counted without permission; names stay hidden.',
    why: 'Device counts separate laptops, desktops and phones.',
    tips: [
      'Browsers already limit this list without permission.',
      'Grant camera and microphone access only to sites that need it: the grant also reveals device names.',
    ],
  },
  'media.codecs': {
    what: 'Which video codecs your device decodes smoothly and power-efficiently.',
    why: 'Reveals hardware video decoding support, which depends on your GPU and OS.',
    tips: [NOTHING],
  },
  'media.canPlayType': {
    what: 'Which audio and video formats your browser says it can play.',
    why: 'Differs by browser, OS and installed codecs.',
    tips: [NOTHING],
  },
  'media.voices': {
    what: 'Text-to-speech voices installed on your system.',
    why: 'Voices depend on OS version and installed language packs. Extra voices make the list distinctive.',
    tips: ['Remove speech voices and language packs you do not use.'],
    phrase: 'speech voices',
  },

  // ── WebRTC ──
  'webrtc.available': {
    what: 'Whether WebRTC, the API behind browser video calls and peer-to-peer connections, is available.',
    why: 'Disabling it is uncommon, but it removes IP leaks entirely.',
    tips: ['Firefox: set media.peerconnection.enabled to false in about:config. This breaks in-browser video calls.'],
  },
  'webrtc.localIps': {
    what: 'Network addresses your browser offers when setting up a peer connection, gathered here with no STUN server.',
    why: 'A real local IP identifies you within your network and is visible even through a VPN. With a STUN server, a tracker would also learn your public IP.',
    tips: [
      'Modern browsers hide local IPs behind random .local names by default.',
      'Brave: Settings → Privacy and security → WebRTC IP handling policy → “Disable non-proxied UDP”.',
      'Firefox: about:config → media.peerconnection.ice.default_address_only = true.',
      'Chrome: Google’s official “WebRTC Network Limiter” extension.',
    ],
    phrase: 'WebRTC network addresses',
  },

  // ── Storage ──
  'storage.available': {
    what: 'Whether localStorage, sessionStorage, IndexedDB, the Cache API and cookies work. Each was tested with a throwaway value that was deleted immediately.',
    why: 'Disabled storage is uncommon and reveals strict privacy settings.',
    tips: ['Block third-party storage rather than all storage.'],
  },
  'storage.quota': {
    what: 'How much storage the browser would let this site use.',
    why: 'Often derived from your disk size, so it can single out devices. A small quota reveals private browsing.',
    tips: ['Some browsers round or standardize it in private modes.'],
  },
  'storage.persisted': {
    what: 'Whether this site has been granted persistent storage.',
    why: 'Almost always false. Informational.',
    tips: ['Nothing to change.'],
  },

  // ── Privacy ──
  'privacy.adblock': {
    what: 'Whether a content blocker hides a bait element with ad-like class names.',
    why: 'Blockers stop many trackers, but their presence (and which filter lists they use) is itself detectable.',
    tips: [
      'Keep your blocker. Blocking tracking scripts helps far more than the bit it adds.',
      'Use one well-known blocker such as uBlock Origin rather than stacking several.',
    ],
  },
  'privacy.permissions': {
    what: 'The current state of a few permissions, queried without showing a prompt.',
    why: 'Granted or denied permissions reveal your habits and settings.',
    tips: ['Reset site permissions you no longer need.'],
  },
  'privacy.trackingHints': {
    what: 'Privacy preference signals your browser sends to every site.',
    why: 'Informational. They ask sites not to track you but do not hide anything.',
    tips: ['Turn on Global Privacy Control if your browser supports it.'],
  },
  'privacy.antiFingerprinting': {
    what: 'A summary of the fingerprinting protections detected across this report.',
    why: 'Shows which high-entropy signals your browser neutralizes.',
    tips: ['See “Reduce your fingerprint” below for steps specific to your browser.'],
  },

  // ── CSS media ──
  'css.colorScheme': {
    what: 'Whether you prefer light or dark mode.',
    why: 'Splits users roughly in two. It can flip automatically with the time of day, so it is not in the stable hash.',
    tips: ['Not worth changing.'],
  },
  'css.reducedMotion': {
    what: 'Your OS “reduce motion” accessibility setting.',
    why: 'A minority turn it on, so it adds a little identifying information.',
    tips: [KEEP_IF_NEEDED],
  },
  'css.reducedTransparency': {
    what: 'Your OS “reduce transparency” setting.',
    why: 'Rarely turned on. A small signal where browsers support it.',
    tips: [KEEP_IF_NEEDED],
  },
  'css.contrast': {
    what: 'Whether you asked for more or less contrast.',
    why: 'A minority setting, so a small signal.',
    tips: [KEEP_IF_NEEDED],
  },
  'css.forcedColors': {
    what: 'Whether a forced color mode, such as Windows contrast themes, is active.',
    why: 'Rare, so it narrows the crowd.',
    tips: [KEEP_IF_NEEDED],
  },
  'css.invertedColors': {
    what: 'Whether the OS inverts screen colors (reported by Safari only).',
    why: 'Rare. A small signal.',
    tips: [KEEP_IF_NEEDED],
  },
  'css.input': {
    what: 'Whether your main input can hover and how precise it is: mouse, touch or stylus.',
    why: 'Separates desktops, phones and touch laptops.',
    tips: ['Nothing to change.'],
  },
  'css.colorGamut': {
    what: 'The widest color range your display supports: sRGB, P3 or Rec. 2020.',
    why: 'Wide-gamut displays are less common and hint at specific device models.',
    tips: [NOTHING],
  },
  'css.dynamicRange': {
    what: 'Whether your display supports HDR.',
    why: 'HDR displays are a minority.',
    tips: [NOTHING],
  },
  'css.displayMode': {
    what: 'Whether this page is shown in a normal tab, fullscreen, or as an installed app.',
    why: 'Almost always “browser”. Informational.',
    tips: ['Nothing to change.'],
  },
  'css.scrollbar': {
    what: 'The width of a classic scrollbar on your system (0 means overlay scrollbars).',
    why: 'Differs by OS and settings, such as macOS “Show scroll bars” or Windows display scaling.',
    tips: ['Default OS settings blend in best.'],
  },
  'css.fontSizes': {
    what: 'Your default font sizes, including the separate monospace default.',
    why: 'Most people never change them, so a changed size stands out.',
    tips: ['Use page zoom instead of changing default font sizes, if that works for you.'],
  },

  // ── Math ──
  'math.fp': {
    what: 'Results of math functions whose last digits vary with the JavaScript engine and the platform’s math library.',
    why: 'Confirms the engine and sometimes the OS or CPU family.',
    tips: [NOTHING],
  },
  'math.errorStack': {
    what: 'The wording of a built-in error message and the format of stack traces.',
    why: 'Identifies the JavaScript engine even when the user agent is spoofed.',
    tips: [NOTHING],
  },
  'math.evalLength': {
    what: 'The length of the source text of the built-in eval function.',
    why: 'A classic one-number engine hint.',
    tips: ['Nothing to change.'],
  },
  'math.collator': {
    what: 'How your browser sorts a fixed list of accented and mixed-script words.',
    why: 'Depends on the Unicode (ICU) library version and your locale’s sorting rules.',
    tips: [NOTHING],
  },

  // ── Misc ──
  'misc.referrer': {
    what: 'The page that linked you here, as scripts can see it.',
    why: 'This site sets a no-referrer policy, so it is usually empty here. Other sites do learn where you came from.',
    tips: ['Browsers trim cross-site referrers to the bare domain by default.'],
  },
  'misc.focus': {
    what: 'Whether this tab was visible and focused when the check ran.',
    why: 'Informational. Trackers use it to tell people from background tabs.',
    tips: ['Nothing to change.'],
  },
  'misc.keyboard': {
    what: 'The characters on a few physical keys, which reveal your keyboard layout (Chromium only).',
    why: 'Non-QWERTY layouts narrow down your country or language.',
    tips: ['Firefox and Safari do not expose it.'],
  },
  'misc.timer': {
    what: 'How precisely performance.now() can measure time.',
    why: 'Browsers coarsen timers to block timing attacks. Very coarse timers point to resistFingerprinting or Tor Browser.',
    tips: ['Nothing to change: your browser picks this.'],
  },
};

const FALLBACK: Explanation = {
  what: 'A value your browser exposes to every page.',
  why: 'Combined with other values, it helps tell browsers apart.',
  tips: [],
};

export function explain(id: string): Explanation {
  return EXPLANATIONS[id] ?? FALLBACK;
}
